<template>
    <div class="tw:flex tw:items-center tw:gap-2 tw:box-border" :class="wrapperClass">
        <button
            :id="id"
            :data-cy="dataCy"
            :disabled="disabled || loading"
            :aria-checked="props.value"
            :aria-label="ariaLabel"
            :aria-labelledby="$slots.default ? labelId : undefined"
            role="switch"
            type="button"
            :class="trackClass"
            @click="toggle"
        >
            <span :class="thumbClass"></span>
        </button>
        <span
            v-if="$slots.default"
            :id="labelId"
            class="tw:text-sm tw:font-medium tw:text-gray-700 tw:uppercase tw:tracking-wide"
            :class="labelClass"
        >
            <slot></slot>
        </span>
    </div>
</template>

<script setup>
    import { ref, watch, computed, toRef } from 'vue'

    const props = defineProps({
        value: {
            type: Boolean,
            default: false
        },
        variant: {
            type: String,
            default: 'primary'
        },
        disabled: Boolean,
        loading: Boolean,
        id: String,
        dataCy: String,
        size: {
            type: String,
            default: 'md'
        },
        ariaLabel: {
            type: String,
            default: undefined
        },
        labelClass: {
            type: String,
            default: ''
        },
        wrapperClass: {
            type: String,
            default: ''
        }
    })

    const emit = defineEmits(['input'])

    // Arranca siempre apagado. El watch sincroniza solo cambios posteriores.
    const visualValue = ref(!!props.value)
    const valueRef = toRef(props, 'value')
    watch(valueRef, val => { visualValue.value = !!val })

    const toggle = () => {
        if (props.disabled || props.loading) return
        visualValue.value = !visualValue.value
        emit('input', visualValue.value)
    }

    const variants = {
        primary: {
            on: 'tw:bg-blue-500',
            off: 'tw:bg-gray-300'
        },
        success: {
            on: 'tw:bg-green-500',
            off: 'tw:bg-gray-300'
        },
        danger: {
            on: 'tw:bg-red-500',
            off: 'tw:bg-gray-300'
        },
        secondary: {
            on: 'tw:bg-gray-600',
            off: 'tw:bg-gray-300'
        }
    }

    const sizes = {
        sm: { track: 'tw:h-4 tw:w-8',  thumb: 'tw:h-3 tw:w-3', translate: 'tw:translate-x-4' },
        md: { track: 'tw:h-5 tw:w-9',  thumb: 'tw:h-4 tw:w-4', translate: 'tw:translate-x-4' },
        lg: { track: 'tw:h-6 tw:w-11', thumb: 'tw:h-5 tw:w-5', translate: 'tw:translate-x-5' },
    }

    const trackClass = computed(() => {
        const v = variants[props.variant] || variants.primary
        const s = sizes[props.size] || sizes.md
        const isDisabled = props.disabled || props.loading

        return [
            'tw:relative tw:inline-flex tw:shrink-0 tw:rounded-full',
            s.track,
            'tw:transition-colors tw:duration-200 tw:ease-in-out',
            'tw:border-2 tw:border-transparent tw:outline-none',
            visualValue.value ? v.on : v.off,
            isDisabled ? 'tw:opacity-50 tw:cursor-not-allowed' : 'tw:cursor-pointer'
        ].join(' ')
    })

    const thumbClass = computed(() => {
        const s = sizes[props.size] || sizes.md

        return [
            'tw:pointer-events-none tw:inline-block tw:rounded-full',
            s.thumb,
            'tw:bg-white tw:shadow tw:ring-0',
            'tw:transition-transform tw:duration-200 tw:ease-in-out',
            visualValue.value ? s.translate : 'tw:translate-x-0'
        ].join(' ')
    })

    const labelId = computed(() => {
        return props.id ? `${props.id}-label` : undefined
    })
</script>